import { ref, computed, provide, inject, type Ref, type InjectionKey } from 'vue'
import type { Document, ImageproxyImagePreset } from '@/types/generated'

export interface PageItem {
  uuid: string
  filename: string
  images: any[]
  thumbnails: ImageproxyImagePreset[] // Add thumbnails array with proper typing
  pageNumber: number
  actualPageNumber: number
  documentUuid: string
  url: string
}

export interface DocumentGroup {
  name: string
  pages: PageItem[]
}

interface DocumentStateContext {
  organizedDocuments: Ref<DocumentGroup[]>
  originalDocumentState: Ref<DocumentGroup[]>
  hasChanges: Ref<boolean>
  caseId: Ref<string>
  allCaseDocuments: Ref<Document[]>

  pageRotations: Ref<Map<string, number>>
  pendingNameChanges: Ref<Map<string, string>>
  isSaving: Ref<boolean>
  setSavingState: (state: boolean) => void

  markAsChanged: () => void
  updateChangeDetection: () => void
  resetState: () => void
}

const DocumentStateContextKey: InjectionKey<DocumentStateContext> = Symbol('DocumentStateContext')

let sharedDocumentState: any = null

export const useDocumentState = (options: { asProvider?: boolean } = {}) => {
  const organizedDocuments = ref<DocumentGroup[]>([])
  const originalDocumentState = ref<DocumentGroup[]>([])
  const hasChanges = ref(false)
  const caseId = ref('')
  const allCaseDocuments = ref<Document[]>([])

  const pageRotations = ref(new Map<string, number>())
  const pendingNameChanges = ref(new Map<string, string>())
  const isSaving = ref(false)

  const markAsChanged = () => {
    hasChanges.value = true
    updateChangeDetection()
  }

  const setSavingState = (state: boolean) => {
    isSaving.value = state
  }

  const updateChangeDetection = () => {
    try {
      const createSafeRepresentation = (docs: DocumentGroup[]) => {
        return docs.map((doc) => ({
          name: doc.name,
          pages: doc.pages.map((page) => ({
            uuid: page.uuid,
            filename: page.filename,
            pageNumber: page.pageNumber,
            actualPageNumber: page.actualPageNumber,
            documentUuid: page.documentUuid,
            url: page.url
          }))
        }))
      }

      const current = JSON.stringify(createSafeRepresentation(organizedDocuments.value))
      const original = JSON.stringify(createSafeRepresentation(originalDocumentState.value))

      // Check if there are any page rotations or pending name changes
      const hasRotations = pageRotations.value.size > 0
      const hasPendingNameChanges = pendingNameChanges.value.size > 0

      hasChanges.value = current !== original || hasRotations || hasPendingNameChanges
    } catch (error) {
      console.warn('Error in change detection, falling back to simple comparison:', error)
      const currentDocs = organizedDocuments.value
      const originalDocs = originalDocumentState.value

      if (currentDocs.length !== originalDocs.length) {
        hasChanges.value = true
        return
      }

      const hasRotations = pageRotations.value.size > 0
      const hasPendingNameChanges = pendingNameChanges.value.size > 0
      hasChanges.value =
        currentDocs.some(
          (doc, index) =>
            doc.name !== originalDocs[index]?.name ||
            doc.pages.length !== originalDocs[index]?.pages.length
        ) ||
        hasRotations ||
        hasPendingNameChanges
    }
  }

  const resetState = () => {
    try {
      organizedDocuments.value = JSON.parse(JSON.stringify(originalDocumentState.value))
    } catch (error) {
      console.warn('Error resetting state, using fallback method:', error)
      organizedDocuments.value = originalDocumentState.value.map((doc) => ({
        name: doc.name,
        pages: doc.pages.map((page) => ({ ...page }))
      }))
    }
    hasChanges.value = false
    pageRotations.value.clear()
    pendingNameChanges.value.clear()
  }

  const findDocumentByName = (name: string): DocumentGroup | undefined => {
    return organizedDocuments.value.find((doc) => doc.name === name)
  }

  const findDocumentByIndex = (index: number): DocumentGroup | undefined => {
    return organizedDocuments.value[index]
  }

  const removeDocument = (index: number): DocumentGroup | undefined => {
    return organizedDocuments.value.splice(index, 1)[0]
  }

  const insertDocument = (index: number, document: DocumentGroup): void => {
    organizedDocuments.value.splice(index, 0, document)
  }

  const generateDocumentName = (baseName: string = 'New Document'): string => {
    let counter = 1
    let name = baseName

    while (organizedDocuments.value.some((doc) => doc.name === name)) {
      name = `${baseName} ${counter}`
      counter++
    }

    return name
  }

  const createDocumentGroup = (name: string, pages: PageItem[]): DocumentGroup => {
    return {
      name,
      pages: [...pages]
    }
  }

  const totalDocuments = computed(() => organizedDocuments.value.length)

  const documentStats = computed(() => {
    const totalPages = organizedDocuments.value.reduce((sum, doc) => sum + doc.pages.length, 0)
    return {
      documentCount: organizedDocuments.value.length,
      totalPages,
      averagePagesPerDocument:
        organizedDocuments.value.length > 0 ? totalPages / organizedDocuments.value.length : 0
    }
  })

  const addPendingNameChange = (documentUuid: string, newName: string) => {
    pendingNameChanges.value.set(documentUuid, newName)
    markAsChanged()
  }

  const removePendingNameChange = (documentUuid: string) => {
    pendingNameChanges.value.delete(documentUuid)
    updateChangeDetection()
  }

  const hasPendingNameChange = (documentUuid: string): boolean => {
    return pendingNameChanges.value.has(documentUuid)
  }

  const getPendingNameChange = (documentUuid: string): string | undefined => {
    return pendingNameChanges.value.get(documentUuid)
  }

  const context: DocumentStateContext = {
    organizedDocuments,
    originalDocumentState,
    hasChanges,
    caseId,
    allCaseDocuments,
    pageRotations,
    pendingNameChanges,
    isSaving,

    markAsChanged,
    updateChangeDetection,
    resetState,
    setSavingState
  }

  const contextWithUtils = {
    ...context,

    findDocumentByName,
    findDocumentByIndex,
    removeDocument,
    insertDocument,
    generateDocumentName,
    createDocumentGroup,

    totalDocuments,
    documentStats,

    // Name change management
    addPendingNameChange,
    removePendingNameChange,
    hasPendingNameChange,
    getPendingNameChange
  }

  if (options.asProvider) {
    provide(DocumentStateContextKey, context)
    // Store as shared state for global access
    sharedDocumentState = contextWithUtils
  }

  return contextWithUtils
}

export const useDocumentStateContext = () => {
  const context = inject(DocumentStateContextKey)

  if (!context) {
    throw new Error('useDocumentStateContext must be used within a DocumentStateProvider')
  }

  return context
}

// Export function to get shared state for global access (e.g., router)
export const getSharedDocumentState = () => {
  return sharedDocumentState
}
